import { useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { lottieAnimations } from '@backrs_ui/common-ui/src/assets/lottieAnimations';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Button, ButtonBase, Theme, Typography } from '@mui/material';
import { SxProps } from '@mui/system';
import Lottie from 'lottie-react';
import { Controller, useForm } from 'react-hook-form';

import { BackrsFormTextField } from '../BackrsForm/BackrsFormTextField';
import { BackrsFormField, BackrsFormValues } from '../BackrsForm/types';

const RESET_STRINGS = appStrings.modules.login.resetPassword;

const MIN_PASSWORD_LENGTH = 8;

const defaultValues: BackrsFormValues = {
  password: '',
  confirmPassword: '',
};

type ResetPasswordProps = Readonly<{
  onSubmit: (password: string, confirmPassword: string) => void;
  onBackToLogin: () => void;
  isLoading?: boolean;
}>;

export const ResetPassword = ({ onSubmit, onBackToLogin, isLoading }: ResetPasswordProps) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = useState(false);

  const { control, handleSubmit, watch, getValues } = useForm<BackrsFormValues>({
    defaultValues,
    mode: 'onTouched',
  });

  const meetsMinLength = (watch('password') ?? '').length >= MIN_PASSWORD_LENGTH;

  const passwordField: BackrsFormField = {
    id: 'reset-password-input',
    name: 'password',
    placeholder: RESET_STRINGS.passwordPlaceholder,
    type: isPasswordVisible ? 'text' : 'password',
    autoComplete: 'new-password',
    autoFocus: true,
    icon: {
      type: 'RIGHT_ICON',
      rightIcon: isPasswordVisible ? 'EyeOff' : 'Eye',
      onRightIconClick: () => setIsPasswordVisible((prev) => !prev),
    },
  };

  const confirmPasswordField: BackrsFormField = {
    id: 'reset-confirm-password-input',
    name: 'confirmPassword',
    placeholder: RESET_STRINGS.confirmPasswordPlaceholder,
    type: isConfirmVisible ? 'text' : 'password',
    autoComplete: 'new-password',
    icon: {
      type: 'RIGHT_ICON',
      rightIcon: isConfirmVisible ? 'EyeOff' : 'Eye',
      onRightIconClick: () => setIsConfirmVisible((prev) => !prev),
    },
  };

  const submit = handleSubmit((data) => onSubmit(data.password, data.confirmPassword));

  return (
    <Box sx={cardSx}>
      <Box sx={headerSx}>
        <Box sx={featuredIconSx}>
          <AppIcon iconName="Lock01" diameter="28px" color="Colors/Text/text-primary" />
        </Box>
        <Box sx={textContainerSx}>
          <Typography
            variant="display-xs-semibold"
            color={(theme) => theme.backrs.colors['Colors/Text/text-primary']}
          >
            {RESET_STRINGS.title}
          </Typography>
          <Typography
            variant="text-md-regular"
            color={(theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
          >
            {RESET_STRINGS.subtitle}
          </Typography>
        </Box>
      </Box>

      <Box component="form" onSubmit={submit} noValidate sx={formSx}>
        <Box sx={fieldsSx}>
          <Box sx={fieldGroupSx}>
            <Typography variant="text-sm-medium" sx={fieldLabelSx}>
              {RESET_STRINGS.passwordPlaceholder}
            </Typography>
            <Controller
              name="password"
              control={control}
              rules={{
                required: RESET_STRINGS.error.passwordRequired,
                minLength: { value: MIN_PASSWORD_LENGTH, message: RESET_STRINGS.error.minLength },
              }}
              render={({ field, fieldState: { error } }) => (
                <BackrsFormTextField
                  field={passwordField}
                  groupedFields={{}}
                  index={0}
                  controllerField={field}
                  error={error?.message}
                />
              )}
            />
          </Box>

          <Box sx={fieldGroupSx}>
            <Typography variant="text-sm-medium" sx={fieldLabelSx}>
              {RESET_STRINGS.confirmPasswordPlaceholder}
            </Typography>
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: RESET_STRINGS.error.confirmRequired,
                validate: (value: string) =>
                  value === getValues('password') || RESET_STRINGS.error.mustMatch,
              }}
              render={({ field, fieldState: { error } }) => (
                <BackrsFormTextField
                  field={confirmPasswordField}
                  groupedFields={{}}
                  index={0}
                  controllerField={field}
                  error={error?.message}
                />
              )}
            />
          </Box>

          <Box sx={checkRowSx}>
            <Box sx={checkCircleSx(meetsMinLength)}>
              <AppIcon iconName="Check" diameter="12px" color="Colors/Base/White" />
            </Box>
            <Typography
              variant="text-sm-regular"
              color={(theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
            >
              {RESET_STRINGS.minLengthCheck}
            </Typography>
          </Box>
        </Box>

        <Button
          type="submit"
          fullWidth
          hierarchy="Primary"
          size="lg"
          id="reset-password-submit-button"
          data-ph-capture-attribute-name="reset-password-submit-button"
          disabled={isLoading}
          icon={isLoading ? 'Leading' : 'Default'}
          endIcon={
            isLoading ? (
              <Box sx={loadingSpinnerSx}>
                <Lottie animationData={lottieAnimations.dotsDotting} loop />
              </Box>
            ) : undefined
          }
        >
          {RESET_STRINGS.submitButton}
        </Button>
      </Box>

      <ButtonBase
        id="reset-password-back-to-login-button"
        sx={backToLoginSx}
        onClick={onBackToLogin}
        disableRipple
      >
        <AppIcon iconName="ArrowLeft" diameter="20px" color="Colors/Text/text-tertiary" />
        <Typography variant="text-sm-semibold" sx={backToLoginLabelSx}>
          {RESET_STRINGS.backToLogin}
        </Typography>
      </ButtonBase>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-4xl'],
  width: '100%',
  maxWidth: '440px',
  boxSizing: 'border-box',
  backgroundColor: {
    xs: backrs.colors['Beige/50'],
    sm: backrs.colors['Colors/Background/bg-primary'],
  },
  border: {
    xs: 'none',
    sm: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
  borderRadius: {
    xs: 0,
    sm: backrs.radius['radius-lg'],
  },
  px: {
    xs: 0,
    sm: backrs.spacing['spacing-4xl'],
  },
  py: {
    xs: backrs.spacing['spacing-2xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const featuredIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '56px',
  height: '56px',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-lg'],
  boxShadow: `2px 2px 0px 0px ${backrs.colors['Colors/Base/Black']}`,
});

const textContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const formSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const fieldsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  width: '100%',
});

const fieldGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  width: '100%',
});

const fieldLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const checkRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const checkCircleSx =
  (isValid: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '20px',
    height: '20px',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: isValid
      ? backrs.colors['Colors/Foreground/fg-success-primary']
      : backrs.colors['Colors/Foreground/fg-disabled-subtle'],
  });

const loadingSpinnerSx: SxProps<Theme> = {
  width: 20,
  height: 20,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const backToLoginSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const backToLoginLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
